import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addFavourite,
  hideToast,
  removeFavourite,
  toggleGuestFavGame,
} from "../../features/slices/UserSlice";
import ConfirmPopup from "./ConfirmPopup";
import ToastMessage from "./ToastMessage";

/**
 * Favourite flow shared by every game list: adding is one tap, removing asks
 * first. Logged-out users get the same flow, stored only in this browser
 * (see utils/guestGames).
 * Render `favouriteUI` once in the list's JSX.
 */
export default function useFavouriteGame() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const showToast = user.madeFavourite || user.madeUnFavourite;
  useEffect(() => {
    if (!showToast) return undefined;
    const timer = setTimeout(() => dispatch(hideToast()), 3000);
    return () => clearTimeout(timer);
  }, [showToast, dispatch]);

  const requestToggle = (game, isFavourite) => {
    if (isFavourite) {
      setPendingRemoval(game);
    } else if (!user.userLoggedIn) {
      dispatch(toggleGuestFavGame(game));
    } else {
      dispatch(addFavourite({ gameId: game.id, game }));
    }
  };

  const confirmRemoval = () => {
    if (!user.userLoggedIn) dispatch(toggleGuestFavGame(pendingRemoval));
    else
      dispatch(
        removeFavourite({ gameId: pendingRemoval.id, game: pendingRemoval })
      );
    setPendingRemoval(null);
  };

  const favouriteUI = (
    <>
      {pendingRemoval && (
        <ConfirmPopup
          title={`Remove ${pendingRemoval.name} from favourites?`}
          message="You can add it back anytime by tapping the star."
          onConfirm={confirmRemoval}
          onCancel={() => setPendingRemoval(null)}
        />
      )}
      {showToast && (
        <ToastMessage
          message={
            user.madeFavourite
              ? "Game Added to Favourites!"
              : "Game Removed from Favourites!"
          }
          onClose={() => dispatch(hideToast())}
        />
      )}
    </>
  );

  return { requestToggle, favouriteUI };
}
